<template>
  <v-main
    app
    class="d-flex flex-column align-center justify-center">
    <div class="mb-8">
      <img src="" />
      <h1 class="text-grey-darken-2">{{ title }}</h1>
    </div>

    <!-- ID・パスワード -->
    <LoginForm
      :width="width"
      v-if="state === AuthState.Unauthorized"
      @submit="authenticateUser"
      @forgot="forgotPassword"
      :error="errorMessage"
      :loading="loading"
      :msg="confirmResultMessage">
    </LoginForm>

    <!-- パスワード変更 -->
    <PasswordChangeForm
      :width="width"
      v-else-if="state === AuthState.NewPasswordRequired"
      @submit="changePassword"
      :error="errorMessage">
    </PasswordChangeForm>

    <!-- TOTP認証（デバイス登録） -->
    <TotpRegisterForm
      v-else-if="state === AuthState.AssociateSecretCode"
      :url="totpUrl"
      @submit="authenticateTotpFirst"
      :error="errorMessage">
    </TotpRegisterForm>

    <!-- TOTP認証-->
    <TotpForm
      v-else-if="state === AuthState.TotpRequired"
      @submit="authenticateTotp"
      :error="errorMessage">
    </TotpForm>

    <!-- パスワード忘れ(認証コード発行要求) -->
    <PasswordForgotForm
      :width="width"
      v-else-if="state === AuthState.ForgotPassword"
      @submit="requestVerificationCode"
      @cancel="cancelForgotPassword"
      :error="errorMessage">
    </PasswordForgotForm>

    <!-- パスワード忘れ(認証コード発行要求) -->
    <PasswordConfirmForm
      :width="width"
      v-else-if="state === AuthState.ConfirmPassword"
      @submit="requestConfirmPassword"
      @cancel="cancelForgotPassword"
      :error="errorMessage"
      :email="confirmEmail"
      :msg="confirmMessage">
    </PasswordConfirmForm>
    <!-- </div> -->
  </v-main>
</template>

<style scoped></style>

<script setup lang="ts">
import { onBeforeMount, ref } from 'vue';

import LoginForm from '@/components/login/LoginForm.vue';
import PasswordChangeForm from '@/components/login/PasswordChangeForm.vue';
import TotpForm from '@/components/login/TotpForm.vue';
import TotpRegisterForm from '@/components/login/TotpRegisterForm.vue';
import PasswordForgotForm from '@/components/login/PasswordForgotForm.vue';
import PasswordConfirmForm from '@/components/login/PasswordConfirmForm.vue';
import { useLoginStore } from '@/stores/login';
import { execute, memberApi, noCapApi } from '@/utils/api';
import { AuthState, Authentication } from '@/utils/authentication';
import { UserType } from '@/types';
import type { EntityMember } from '@/utils/api/api';
import type { EntityAdministrator } from '@/utils/api/maintenance/api';

const props = defineProps<{
  user?: UserType;
}>();

import { computed } from 'vue';
import { useDisplay } from 'vuetify';

const loginStore = useLoginStore();

let auth = new Authentication(props.user === UserType.ADMIN);
let state = ref(AuthState.Unauthorized);
let errorMessage = ref('');
let totpUrl = ref('');
let loading = ref(false);

// パスワード忘れ周り
let confirmEmail = ref('');
let confirmMessage = ref('');
let confirmResultMessage = ref('');

const title = process.env.VITE_APP_NAME + (props.user === UserType.ADMIN ? '【管理】' : '');

onBeforeMount(() => {
  console.debug(`[LoginView] onBeforeMount`);

  // ログイン情報クリア
  loginStore.logout();

  const urlParams = new URL(window.location.href).searchParams;

  // 招待状IDを保存
  const invitationId = urlParams.get('invitationId');
  if (invitationId) {
    loginStore.setInvitationId(invitationId);
  }
});

function authenticateUser(email: string, password: string) {
  loading.value = true;

  // Cognito認証
  auth.authenticateUser(email, password, {
    // 認証成功
    onSuccess: async function (): Promise<void> {
      // ログイン処理
      if (props.user === UserType.ADMIN) {
        // メンテナンス用のログイン処理（管理者）
        loginAdmin();
      } else {
        // ミーティング用のログイン処理（一次／二次専門組織）
        loginMeeting();
      }
    },
    // 認証失敗
    onFailure: function (error: string): void {
      loading.value = false;

      switch (state.value) {
        case AuthState.Unauthorized:
          errorMessage.value = 'ユーザー認証に失敗しました。';
          break;
        case AuthState.NewPasswordRequired:
          errorMessage.value = 'パスワードの更新に失敗しました。';
          break;
        case AuthState.AssociateSecretCode:
        case AuthState.TotpRequired:
          errorMessage.value = 'ワンタイムパスワードの認証に失敗しました。';
          break;
      }
    },
    // パスワード変更
    newPasswordRequired: function (): void {
      errorMessage.value = '';
      state.value = AuthState.NewPasswordRequired;
      loading.value = false;
    },
    // TOTP認証（初回）
    associateSecretCode: function (url: string): void {
      totpUrl.value = url;
      state.value = AuthState.AssociateSecretCode;
      loading.value = false;
    },
    // TOTP認証
    totpRequired: function (): void {
      state.value = AuthState.TotpRequired;
      loading.value = false;
    }
  });
}

/**
 * パスワードを変更する
 * @param password 新しいパスワード
 */
function changePassword(password: string) {
  errorMessage.value = '';
  auth.changePassword(password);
}

/**
 * TOTP認証（初回）
 * @param totp TOTP
 */
function authenticateTotpFirst(totp: string) {
  errorMessage.value = '';
  auth.authenticateTotp(totp, true);
}

/**
 * TOTP認証
 * @param totp TOTP
 */
function authenticateTotp(totp: string) {
  errorMessage.value = '';
  auth.authenticateTotp(totp);
}

/**
 * パスワードわすれ画面へ状態遷移
 */
function forgotPassword() {
  errorMessage.value = '';
  state.value = AuthState.ForgotPassword;
}

/**
 * パスワードわすれ画面キャンセル
 */
function cancelForgotPassword() {
  state.value = AuthState.Unauthorized;
}

/**
 * 認証コード発行要求
 * @param email
 */
function requestVerificationCode(email: string) {
  loading.value = true;
  confirmMessage.value = '';
  errorMessage.value = '';

  auth.forgotPassword(email, {
    onSuccess: (data: string) => {
      alert('onSuccess:' + JSON.stringify(data));
      state.value = AuthState.ConfirmPassword;
      loading.value = false;
    },
    onFailure: (err: Error) => {
      errorMessage.value = err.message || JSON.stringify(err);
      loading.value = false;
    },
    inputVerificationCode: (data: any) => {
      console.log(JSON.stringify(data));
      confirmEmail.value = email;
      if (
        data?.CodeDeliveryDetails &&
        data.CodeDeliveryDetails?.AttributeName == 'email' &&
        data.CodeDeliveryDetails?.Destination
      )
        confirmMessage.value = `${data.CodeDeliveryDetails.Destination}へメールを送信しました`;
      else confirmMessage.value = '認証コードを送信しました';
      state.value = AuthState.ConfirmPassword;
      loading.value = false;
    }
  });
}

/**
 * パスワード忘れ・パスワード変更
 * @param email
 * @param code
 * @param newPassword
 */
function requestConfirmPassword(email: string, code: string, newPassword: string) {
  loading.value = true;
  confirmResultMessage.value = '';
  errorMessage.value = '';

  auth.confirmPassword(email, code, newPassword, {
    onSuccess: (success: string): void => {
      confirmResultMessage.value =
        'パスワードを更新しました。新しいパスワードでログインしてください。';
      state.value = AuthState.Unauthorized;
      loading.value = false;
    },
    onFailure: (err: Error): void => {
      errorMessage.value = `パスワードの更新に失敗しました:${err.message || JSON.stringify(err)}`;
      loading.value = false;
    }
  });
}

/**
 * [一次専門組織・二次専門組織] ログイン処理
 */
async function loginMeeting() {
  console.debug('[LoginView] loginMeeting');

  errorMessage.value = '';
  loading.value = true;

  execute(() => memberApi.login())
    .then((data: EntityMember) => {
      console.info('ログイン成功');
      console.debug(data);

      // ログイン情報を保存
      loginStore.login(data);

      // 会議室選択画面に遷移
      window.location.href = 'meeting/';
    })
    .catch((err: unknown) => {
      console.info(err);
      console.error('ログイン失敗');
      // ID・パスワード入力に戻る
      state.value === AuthState.Unauthorized;
      errorMessage.value = 'ログイン処理に失敗しました。';
    })
    .finally(() => {
      loading.value = false;
    });
}
//レスポンシブ
const { name } = useDisplay();

const width = computed(() => {
  switch (name.value) {
    case 'xs':
      return 320;
    case 'sm':
      return 620;
    case 'md':
      return 820;
    case 'lg':
      return 1020;
    case 'xl':
      return 1220;
    case 'xxl':
      return 1420;
  }
});

/**
 * [管理者] ログイン処理
 */
async function loginAdmin() {
  console.debug('[LoginView] loginAdmin');

  errorMessage.value = '';
  loading.value = true;

  execute(() => noCapApi.login())
    .then((data: EntityAdministrator) => {
      console.info('[管理者] ログイン成功');
      console.debug(data);

      // ログイン情報を保存
      loginStore.loginAdmin(data);

      // ホーム画面に遷移
      window.location.href = '/mainte/contents/';
    })
    .catch((err: unknown) => {
      console.info(err);
      console.error('ログイン失敗');

      // ID・パスワード入力に戻る
      state.value === AuthState.Unauthorized;
      errorMessage.value = 'ログイン処理に失敗しました。';
    })
    .finally(() => {
      loading.value = false;
    });
}
</script>
